import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import IconView from '@/assets/icons/icon-view.svg?react';
import IconViewOffSlash from '@/assets/icons/icon-view-off-slash.svg?react';
import IconMoreHorizontal from '@/assets/icons/icon-more-horizontal.svg?react';
import IconSquareLock from '@/assets/icons/icon-square-lock.svg?react';
import IconSquareUnlock from '@/assets/icons/icon-square-unlock.svg?react';

import { useLayer } from '@craftjs/layers';

import { LayerDropdown } from '../LayerDropdown/LayerDropdown';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import useEditableLayer from '@/hooks/useEditableLayer';
import { useLayerNodeState } from '@/hooks/useLayerNodeState';
import { useCanvasFocus } from '@/context/CanvasFocusContext';
import { shouldIgnoreLayerRowDoubleClick } from '../layerRowUtils';

interface LayerProps {
  className: string;
  onClick?: () => void;
  text: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  type: 'default' | 'selected' | 'empty';
  depthLevel?: number;
  isActive?: boolean;
  /** When true, this row is a descendant of the active container and should not round on hover */
  isHighlighted?: boolean;
  /** Suppress rounded corners even when active (e.g., when parent collapse is active) */
  suppressRounded?: boolean;
  /** When true, this row is a sibling under the active parent (leaf selected) and gets gray bg */
  mutedSibling?: boolean;
}

export const Layer: React.FC<LayerProps> = ({
  className = '',
  onClick,
  icon: Icon,
  text,
  type = 'default',
  depthLevel = 0,
  isActive = false,
  isHighlighted = false,
  suppressRounded = false,
  mutedSibling = false,
}) => {
  let id: string | undefined;
  let layerHeader: <B extends HTMLElement | React.ReactElement>(element: B) => B = (ref) => ref;
  let drag = <T extends HTMLElement>(ref: T) => ref;
  let isInsideCraft = true;

  try {
    const layer = useLayer();
    id = layer?.id;
    layerHeader = layer?.connectors.layerHeader;
    drag = layer?.connectors.drag;
  } catch {
    isInsideCraft = false;
  }

  const { isHidden: isLayerHidden, isLocked: isLayerLocked } = useLayerNodeState(id);
  const [hidden, setHidden] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    setHidden(isLayerHidden);
    setLocked(isLayerLocked);
  }, [isLayerHidden, isLayerLocked]);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { toggleVisibility, toggleLock } = useCraftHelper();
  const { focusCanvasOnSelectedNode } = useCanvasFocus();
  const {
    inputTitleRef,
    isEditing,
    textValue,
    handleOnBlur,
    handleOnChange,
    handleOnKeyUp,
    handleEnterEditMode,
  } = useEditableLayer({ text, id: id ?? '' });

  // Consistent indentation: base 8px + 12px per depth level (clamped)
  const normalizedDepth = Math.max(0, depthLevel ?? 0);
  const paddingStyle = {
    paddingLeft: 8 + 12 * normalizedDepth,
  };

  const handleToggleNodeVisibility = () => {
    if (!id) return;
    const newValue = toggleVisibility(id);
    setHidden(newValue);
  };

  const handleClick = () => {
    if (onClick) onClick();
  };
  const handleToggleLock = () => {
    if (!id) {
      console.warn('Lock toggle is disabled outside Craft or without a valid node ID.');
      return;
    }
    toggleLock(id);
    setLocked((prev) => !prev); // Toggle the locked state
  };

  const handleRowDoubleClick = (e: React.MouseEvent) => {
    if (shouldIgnoreLayerRowDoubleClick(e)) return;
    e.stopPropagation();
    if (id) focusCanvasOnSelectedNode(id);
  };

  return (
    <div
      onClick={(event) => {
        event.stopPropagation();
        handleClick();
      }}
      onDoubleClick={handleRowDoubleClick}
      style={paddingStyle}
      className={clsx(
        'group animate-layer-enter flex cursor-pointer items-center justify-between pr-2 transition-all motion-reduce:animate-none',
        className,
        {
          'bg-indigo-50 text-neutral-500 hover:bg-indigo-200': type === 'default' && !isActive,
          'bg-indigo-200 text-indigo-500 dark:text-indigo-800': type === 'selected' && isActive,
          'bg-indigo-200': type === 'default' && dropdownOpen && !isActive,
          'bg-indigo-200 text-neutral-400 hover:bg-indigo-200':
            (type === 'default' || type === 'empty') && hidden === true && !isActive,
          'text-neutral-500 hover:bg-indigo-200': type === 'empty',
          'rounded-md': isActive && !suppressRounded,
        },
        // Gray background for siblings when a leaf under the same parent is selected
        { 'bg-neutral-100 hover:bg-indigo-200': mutedSibling && !isActive && !hidden },
        { 'hover:rounded-md': !isHighlighted && !isActive && !mutedSibling && !suppressRounded },
      )}
      ref={(ref) => {
        if (ref && isInsideCraft) {
          layerHeader(drag(ref));
        }
      }}
    >
      {(Icon || text !== '') && (
        <div className="flex min-w-0 flex-grow-1 items-center gap-2">
          {/* Fixed left gutter (caret width) to align with collapses */}
          <div className="w-5 flex-shrink-0" />
          <div
            data-layer-rename
            className="flex min-w-0 flex-grow-1 items-center gap-2 py-2"
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleEnterEditMode();
            }}
          >
            {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
            <input
              ref={inputTitleRef}
              className={`${isEditing ? '' : 'hidden'} w-[70%] flex-grow-1 rounded border border-indigo-500 bg-white text-xs text-black`}
              type="text"
              value={textValue}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              onKeyUp={handleOnKeyUp}
            />
            {!isEditing && <span className="block flex-grow-1 truncate text-xs">{text}</span>}
          </div>
        </div>
      )}
      {!isEditing && (
        <div
          data-layer-actions
          className={clsx('flex shrink-0 items-center gap-2 transition-all', {
            'opacity-0 group-hover:opacity-100':
              type === 'default' || (type === 'empty' && hidden !== true),
            'opacity-100': dropdownOpen,
          })}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (isInsideCraft) handleToggleNodeVisibility();
              else setHidden(!hidden);
            }}
          >
            {hidden ? (
              <IconViewOffSlash className="h-4 w-4 cursor-pointer" />
            ) : (
              <IconView className="h-4 w-4 cursor-pointer" />
            )}
          </button>
          <button type="button" onClick={handleToggleLock}>
            <IconSquareLock
              className={clsx('h-4 w-4 cursor-pointer', {
                hidden: !locked, // Show lock icon only when locked
              })}
            />
            <IconSquareUnlock
              className={clsx('h-4 w-4 cursor-pointer', {
                hidden: locked, // Show unlock icon only when unlocked
              })}
            />
          </button>
          <LayerDropdown
            className=""
            onClick={() => {}}
            setDropdownOpen={setDropdownOpen}
            childrenTrigger={<IconMoreHorizontal className="h-4 w-4" />}
            dropdownOpen={dropdownOpen}
            onEnterEditTitleMode={handleEnterEditMode}
          />
        </div>
      )}
    </div>
  );
};
